import { AmsPermissionClaimType, authService } from '../../services/Auth';
import { Badge, Button, ButtonGroup, Card, CardBody, Col, Label, Modal, Row } from 'reactstrap';
import {
    InstitutionPeopleDto,
    ProfessionAddOnTrack,
    ProfessionDto,
    ProfessionLevel,
    ProfessionsConcentrationDto,
    ProgramDto,
} from '../../common/Types';
import { DegreeType, ExtensionStatus, InstitutionPersonRole, ProgramStatus } from '../../common/Data';
import React, { useCallback, useEffect, useState } from 'react';

import { NavLink } from 'react-router-dom';
import NewProgram from '../../components/NewProgram';
import styled from 'styled-components';
import { professionService } from '../../services/ProfessionAdministration';
import { toast } from 'react-toastify';
import { programService } from '../../services/ProgramService';

const StyledCard = styled(Card)`
    margin: 8px;
    display: flex;
    flex-direction: column;
    color: #6c757d;

    h4 {
        white-space: nowrap;
        padding-left: 32px;
    }

    .ribbon-box {
        display: flex;
        flex-direction: column;
    }

    .title-row {
        display: flex;
    }

    .button-row {
        display: flex;
        justify-content: flex-end;
        align-items: flex-end;
        font-size: 24px;
        flex-direction: column;
        flex-grow: 1;
    }

    :hover {
        box-shadow: 0 0 8px 0 rgba(154, 161, 171, 0.3);
    }
`;
type Props = {
    programs: ProgramDto[] | undefined;
    people: InstitutionPeopleDto[] | undefined;
    institutionId: number;
};

const Programs = (props: Props) => {
    const { people, institutionId } = props;
    const [levels, setLevels] = useState<ProfessionLevel[]>([]);
    const [professionConcentrations, setProfessionConcentrations] = useState<ProfessionsConcentrationDto[]>([]);
    const [professionAddOns, setProfessionAddOns] = useState<ProfessionAddOnTrack[]>([]);
    const [concentrationsLoaded, setConcentrationsLoaded] = useState(false);
    const [addOnsLoaded, setAddOnsLoaded] = useState(false);
    const [professions, setProfessions] = useState<ProfessionDto[]>([]);
    const [professionIds, setProfessionIds] = useState<number[]>([]);
    const [extensionTitles, setExtensionTitles] = useState<Record<number, string>>({});
    const [loadingExtensions, setLoadingExtensions] = useState(true);
    const [programs, setPrograms] = useState<ProgramDto[]>(props.programs ?? []);
    const [showAll, setShowAll] = useState<boolean>(false);
    const [showNewProgram, setShowNewProgram] = useState<boolean>(false);
    const toggleShowNewProgram = () => setShowNewProgram(!showNewProgram);

    const showProgram = useCallback(
        (program: ProgramDto): boolean => {
            if (!authService.hasPermission(AmsPermissionClaimType, 'systemadmin') || !showAll) {
                if (program.activeStatus === ProgramStatus['Not Applicable'] || !program.active) {
                    return false;
                }
            }
            return true;
        },
        [showAll],
    );

    useEffect(() => {
        const sortedPrograms = props.programs
            ?.filter((p) => showProgram(p))
            .sort((a, b) => {
                // First sort by status order
                const statusOrderA = getStatusSortOrder(a.activeStatus);
                const statusOrderB = getStatusSortOrder(b.activeStatus);
                
                if (statusOrderA !== statusOrderB) {
                    return statusOrderA - statusOrderB;
                }
                
                // If status is the same, sort alphabetically by profession name
                // Try to get profession names if professions are loaded
                if (professions && professions.length > 0) {
                    const professionA = a.professionId ? 
                        professions.find(p => p.professionId === a.professionId)?.title || '' : '';
                    const professionB = b.professionId ? 
                        professions.find(p => p.professionId === b.professionId)?.title || '' : '';
                    
                    if (professionA || professionB) {
                        return professionA.localeCompare(professionB);
                    }
                }
                
                // Fall back to program title if profession not found
                return (a.title || '').localeCompare(b.title || '');
            }) ?? [];
            
        setPrograms(sortedPrograms);
    }, [props.programs, showAll, showProgram, professions]);

    useEffect(() => {
        const ids = programs.map((p) => p.professionId).filter((id): id is number => id !== null && id !== undefined);
        setProfessionIds(ids);
    }, [programs]);

    useEffect(() => {
        professionService
            .getProfessions()
            .then((response) => {
                setProfessions(response);
            })
            .catch(() => {
                toast.error('Failed to load professions.');
            });
        professionService
            .getAllLevels()
            .then((response) => {
                setLevels(response);
            })
            .catch(() => {
                toast.error('Failed to load levels.');
            });
    }, []);

    useEffect(() => {
        if (professionIds.length > 0) {
            professionService
                .getProfessionConcentrations(professionIds)
                .then((response) => {
                    setProfessionConcentrations(response);
                    setConcentrationsLoaded(true);
                })
                .catch(() => {
                    toast.error('Failed to load concentrations.');
                    setConcentrationsLoaded(true); // Mark as loaded even on error
                });
        } else {
            setConcentrationsLoaded(true); // No professions, nothing to load
        }
        professionService
            .getAllProfessionAddOnTracks()
            .then((response) => {
                setProfessionAddOns(response);
                setAddOnsLoaded(true);
            })
            .catch(() => {
                toast.error('Failed to load add-ons.');
                setAddOnsLoaded(true); // Mark as loaded even on error
            });
    }, [professionIds]);

    const getExtensions = useCallback(
        async (program: ProgramDto): Promise<string> => {
            if (!program || !professionConcentrations || !professionAddOns) {
                return '';
            }

            try {
                const extensions = await programService.getProgramExtensions(program.programId);
                // Filter extensions that are not deleted and not removed
                const activeExtensions = extensions.filter((ext) => !ext.isDeleted && ext.activeStatus !== ExtensionStatus.Removed);

                const titles = activeExtensions.map((ext) => {
                    if (ext.concentrationId) {
                        return professionConcentrations.find((c) => c.concentrationId === ext.concentrationId)?.title;
                    }

                    if (ext.addOnTrackId) {
                        return professionAddOns.find((a) => a.addOnTrackId === ext.addOnTrackId)?.title;
                    }

                    return null;
                });

                return titles.filter((title): title is string => !!title).join(', ');
            } catch (error) {
                toast.error('Error loading program extensions');
                return '';
            }
        },
        [professionConcentrations, professionAddOns],
    );

    useEffect(() => {
        if (
            !programs ||
            programs.length === 0 ||
            !concentrationsLoaded ||
            !addOnsLoaded
        ) {
            return;
        }

        const loadExtensions = async () => {
            setLoadingExtensions(true);
            const titles: Record<number, string> = {};

            for (const program of programs) {
                const title = await getExtensions(program);
                titles[program.programId] = title;
            }
            setExtensionTitles(titles);
            setLoadingExtensions(false);
        };

        loadExtensions();
    }, [programs, concentrationsLoaded, addOnsLoaded, getExtensions]);

    const getProgramDirector = (programId: number): string => {
        const person = people?.find(
            (p) =>
                p.active === true &&
                p.programId === programId &&
                p.people?.active === true &&
                p.roleId === InstitutionPersonRole['Program Director'],
        );

        if (person?.people) {
            return `${person.people.firstName} ${person.people.lastName}`;
        }

        return ``;
    };

    const getDean = (programId: number): string => {
        const person = people?.find(
            (p) =>
                p.active === true &&
                p.programId === programId &&
                p.people?.active === true &&
                p.roleId === InstitutionPersonRole.Dean,
        );

        if (person?.people) {
            return `${person.people.firstName} ${person.people.lastName}`;
        }

        return ``;
    };

    const getAward = (program: ProgramDto): string => {
        const awards = [
            program.degreeTypeId === DegreeType.Certificate ? 'Certificate' : null,
            program.degreeTypeId === DegreeType.Associate ? 'Associate' : null,
            program.degreeTypeId === DegreeType.Diploma ? 'Diploma' : null,
            program.degreeTypeId === DegreeType.Baccalaureate ? 'Baccalaureate' : null,
            program.degreeTypeId === DegreeType.Masters ? 'Masters' : null,
        ];

        return awards.filter((a) => a).join(', ');
    };

    const isInactiveOrDeleted = (program: ProgramDto): boolean => {
        return !program.active || program.activeStatus === ProgramStatus['Not Applicable'];
    };

    const getStatusBadgeColor = (status: number | null | undefined): { color: string; style: React.CSSProperties } => {
        if (status === null || status === undefined) return { color: 'primary', style: {} };
        
        const statusName = ProgramStatus[status];
        
        // Always return a color prop to prevent defaulting to 'secondary'
        // Use empty string for custom colors so no Bootstrap class is applied
        switch (statusName) {
            case 'Pending':
                return { color: '', style: { backgroundColor: '#6C7393', borderColor: '#6C7393' } };
            case 'Initial':
                return { color: '', style: { backgroundColor: '#F7B84A', borderColor: '#F7B84A' } };
            case 'Continuing':
                return { color: '', style: { backgroundColor: '#1ABC9C', borderColor: '#1ABC9C' } };
            case 'Withdraw':
            case 'Probation':
            case 'Voluntary Withdraw':
            case 'Letter of Review':
            case 'Withhold':
                return { color: '', style: { backgroundColor: '#F1556C', borderColor: '#F1556C' } };
            default:
                return { color: 'primary', style: {} }; // Use default blue color
        }
    };

    const getStatusSortOrder = (status: number | null | undefined): number => {
        // Define the sort order based on the enum values
        // Lower numbers sort first
        const statusOrder: { [key: number]: number } = {
            18: 1,  // Pending
            3: 2,   // Initial
            2: 3,   // Continuing
            4: 4,   // Probation
            13: 5,  // Withhold
            7: 6,   // Withdraw
            9: 7,   // Voluntary Withdraw
            15: 8,  // Transfer of Sponsorship - Initial
            16: 9,  // Transfer of Sponsorship - Continuing
            17: 10, // Transfer of Sponsorship - Probation
            11: 11, // Letter of Review
            6: 12,  // Transfer
            8: 13,  // Expire
            10: 14, // Inactive
            12: 15, // Not Applicable
        };

        if (status === null || status === undefined) return 999; // Sort null/undefined to the end
        return statusOrder[status] || 999; // Default to end if status not found
    };

    const getProfessionName = (program: ProgramDto): string => {
        const profession =
            program.professionId &&
            professions &&
            program.professionId > 0 &&
            professions.find((pr) => pr.professionId === program.professionId)?.title;
        if (!profession) {
            return '';
        }
        const level =
            program.professionLevelId && levels ? levels.find((l) => l.id === program.professionLevelId)?.title : null;

        return level ? `${profession} - ${level}` : profession;
    };

    return (
        <>
            <Row>
                <Col>
                    <Card>
                        <CardBody className={'pb-0'}>
                            <h4 className={'card-title d-flex justify-content-between align-items-center'}>
                                <div className="d-flex align-items-center">
                                    <span>{`Programs`}</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    {authService.hasPermission(AmsPermissionClaimType, 'systemadmin') && (
                                        <>
                                            <Button
                                                onClick={() => toggleShowNewProgram()}
                                                type={'button'}
                                                color={'primary'}
                                            >
                                                <i className={'mdi mdi-plus'} />
                                                <span className={'ml-1'}>{`New Program`}</span>
                                            </Button>
                                            <Button color="link" onClick={() => setShowAll(!showAll)}>
                                                {`Show All`}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </h4>
                        </CardBody>
                        <div className={'title-responsive'} style={{ maxHeight: '650px', overflow: 'auto' }}>
                            <table className={'table table-striped'}>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>{`Profession`}</th>
                                        <th>{`Program`}</th>
                                        <th>{`Concentration/Add-On`}</th>
                                        <th>{`Program Director`}</th>
                                        <th>{`Dean`}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                {(programs?.length || 0) > 0 && (
                                    <tbody>
                                        {programs.map((p) => {
                                            const badgeProps = getStatusBadgeColor(p.activeStatus);
                                            return (
                                                <tr
                                                    key={p.programId}
                                                    className={isInactiveOrDeleted(p) ? 'inactive-deleted' : ''}
                                                >
                                                    <td className={'align-middle'}>
                                                        <Label>
                                                            <Badge 
                                                                color={badgeProps.color} 
                                                                className={'p-1'}
                                                                style={badgeProps.style}
                                                            >
                                                                {p.activeStatus
                                                                    ? ProgramStatus[p.activeStatus]
                                                                    : 'No Status Set'}
                                                            </Badge>
                                                        </Label>
                                                    </td>
                                                <td className="align-middle">{getProfessionName(p)}</td>
                                                <td className={'align-middle'}>
                                                    <NavLink
                                                        to={`/institution/${p.institutionId}/program/${p.programId}`}
                                                    >
                                                        {p.activeStatus === ProgramStatus['Not Applicable']
                                                            ? (p.title || `[${getProfessionName(p)} - ${getAward(p)}]`)
                                                            : (p.programName
                                                                ? p.programName
                                                                : `[${getProfessionName(p)} - ${getAward(p)}]`)}
                                                    </NavLink>
                                                </td>
                                                <td className={`align-middle`}>
                                                    {loadingExtensions
                                                        ? 'Loading...'
                                                        : (extensionTitles[p.programId] ?? '')}
                                                </td>
                                                <td className={'align-middle'}>{getProgramDirector(p.programId)}</td>
                                                <td className={'align-middle'}>{getDean(p.programId)}</td>
                                                <td className={'align-middle'}>
                                                    <ButtonGroup>
                                                        <NavLink
                                                            to={`/institution/${p.institutionId}/program/${p.programId}`}
                                                            className={'btn btn-info'}
                                                        >
                                                            {`View`}
                                                        </NavLink>
                                                    </ButtonGroup>
                                                </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                )}
                                {(programs?.length || 0) === 0 && (
                                    <tbody>
                                        <tr>
                                            <td colSpan={8}>{`No Programs`}</td>
                                        </tr>
                                    </tbody>
                                )}
                            </table>
                        </div>
                    </Card>
                </Col>
            </Row>
            {authService.hasPermission(AmsPermissionClaimType, 'systemadmin') && (
                <Modal size={'lg'} isOpen={showNewProgram} toggle={toggleShowNewProgram}>
                    <NewProgram
                        onCanceled={toggleShowNewProgram}
                        onSaved={(newProgram) => setPrograms([...programs, { ...newProgram }])}
                        institutionId={institutionId}
                    />
                </Modal>
            )}
        </>
    );
};

export default Programs;
